import { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import axios from 'axios';
import { Users, Trash2, Loader2, Search, Check, X, Clock, MousePointerClick } from 'lucide-react';
import { toast } from 'sonner';
import { AdminAlertConfig } from '../components/AdminAlertConfig';
import { CountryAnalytics } from '../components/CountryAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
];

export default function AdminStudents() {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, id: null, name: '' });
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        fetchStudents();
    }, []);

    const fetchStudents = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/students`, { withCredentials: true });
            setStudents(data);
        } catch (e) {
            console.error('Error fetching students:', e);
            toast.error('Failed to load students');
        } finally {
            setLoading(false);
        }
    };

    const approveStudent = async (studentId) => {
        setActionLoading(true);
        try {
            await axios.put(`${API}/admin/students/${studentId}/approve`, {}, { withCredentials: true });
            toast.success('Student approved — they now have access');
            fetchStudents();
        } catch (e) {
            toast.error('Failed to approve student');
        } finally {
            setActionLoading(false);
        }
    };

    const rejectStudent = async () => {
        setActionLoading(true);
        try {
            await axios.put(`${API}/admin/students/${confirmDialog.id}/reject`, {}, { withCredentials: true });
            toast.success('Student rejected');
            setConfirmDialog({ open: false, action: null, id: null, name: '' });
            fetchStudents();
        } catch (e) {
            toast.error('Failed to reject student');
        } finally {
            setActionLoading(false);
        }
    };

    const deleteStudent = async () => {
        setActionLoading(true);
        try {
            await axios.delete(`${API}/admin/students/${confirmDialog.id}`, { withCredentials: true });
            toast.success('Student removed');
            setConfirmDialog({ open: false, action: null, id: null, name: '' });
            fetchStudents();
        } catch (e) {
            toast.error('Failed to remove student');
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const getPhaseColor = (phase) => ({
        safe_start: '#3B82F6',
        building_consistency: '#8B5CF6',
        capital_confidence: '#D4AF37',
    })[phase] || '#94A3B8';

    const formatPhaseName = (phase) => phase ? phase.replace(/_/g, ' ').toUpperCase() : 'N/A';

    const getStatusStyle = (status) => {
        const styles = {
            approved: { bg: 'rgba(16, 185, 129, 0.1)', color: '#10B981', border: 'rgba(16, 185, 129, 0.2)' },
            pending: { bg: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B', border: 'rgba(245, 158, 11, 0.2)' },
            rejected: { bg: 'rgba(239, 68, 68, 0.1)', color: '#EF4444', border: 'rgba(239, 68, 68, 0.2)' },
        };
        return styles[status] || styles.approved;
    };

    const filteredStudents = students
        .filter(s => statusFilter === 'all' || s.status === statusFilter)
        .filter(s =>
            s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.email.toLowerCase().includes(searchTerm.toLowerCase())
        );

    const pendingCount = students.filter(s => s.status === 'pending').length;

    if (loading) {
        return (
            <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
                <Header />
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="w-10 h-10 animate-spin" style={{ color: 'var(--primary-color)' }} />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
            <Header />

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <AdminAlertConfig />
                <CountryAnalytics />
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }} data-testid="students-management-title">
                            Manage Students
                        </h1>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {students.length} total • {pendingCount > 0 && (
                                <span style={{ color: '#F59E0B', fontWeight: 600 }}>{pendingCount} pending approval</span>
                            )}
                            {pendingCount === 0 && 'No pending approvals'}
                        </p>
                    </div>

                    <div className="relative w-full sm:w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search students..."
                            className="input-field w-full pl-10"
                            data-testid="search-students-input"
                        />
                    </div>
                </div>

                {/* Status Filter */}
                <div className="flex flex-wrap gap-2 mb-6" data-testid="status-filter-tabs">
                    {STATUS_FILTERS.map(f => {
                        const count = f.value === 'all' ? students.length : students.filter(s => s.status === f.value).length;
                        const active = statusFilter === f.value;
                        return (
                            <button
                                key={f.value}
                                onClick={() => setStatusFilter(f.value)}
                                className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                                style={{
                                    backgroundColor: active ? 'var(--primary-color)' : 'var(--background-elevated)',
                                    color: active ? 'var(--primary-text)' : 'var(--text-secondary)',
                                    border: `1px solid ${active ? 'var(--primary-color)' : 'var(--border-subtle)'}`
                                }}
                                data-testid={`filter-${f.value}`}
                            >
                                {f.label}
                                <span className="ml-1 px-1.5 py-0.5 rounded text-xs" style={{
                                    backgroundColor: active ? 'rgba(0,0,0,0.2)' : 'var(--background-surface)',
                                    color: active ? 'var(--primary-text)' : 'var(--text-muted)'
                                }}>{count}</span>
                            </button>
                        );
                    })}
                </div>

                {students.length === 0 ? (
                    <div className="card-surface p-12 text-center" data-testid="no-students-message">
                        <Users className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                        <p className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>No students enrolled</p>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Students will appear here once they register</p>
                    </div>
                ) : filteredStudents.length === 0 ? (
                    <div className="card-surface p-12 text-center">
                        <p className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>No results</p>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Try a different filter or search term</p>
                    </div>
                ) : (
                    <div className="card-surface overflow-hidden" data-testid="students-table">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="table-header">
                                        <th className="text-left py-3 px-4">Student</th>
                                        <th className="text-left py-3 px-4">Status</th>
                                        <th className="text-left py-3 px-4">Broker</th>
                                        <th className="text-left py-3 px-4">Proof</th>
                                        <th className="text-left py-3 px-4">Phase</th>
                                        <th className="text-left py-3 px-4">Enrolled</th>
                                        <th className="text-center py-3 px-4">Lessons</th>
                                        <th className="text-right py-3 px-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredStudents.map((student) => {
                                        const statusStyle = getStatusStyle(student.status);
                                        const brokerColors = { ywo: '#00E5FF', exness: '#FFB800' };
                                        const brokerColor = brokerColors[student.broker] || '#8A8FA3';
                                        return (
                                            <tr key={student.id} className="table-row" data-testid={`student-row-${student.id}`}>
                                                <td className="py-4 px-4">
                                                    <p className="font-medium flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                                                        {student.name}
                                                        {student.country && (
                                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-black tracking-wide" style={{ backgroundColor: 'var(--primary-muted)', color: 'var(--primary-color)', border: '1px solid var(--border-highlight)' }} data-testid={`student-country-${student.id}`}>
                                                                {student.country}
                                                            </span>
                                                        )}
                                                    </p>
                                                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{student.email}</p>
                                                </td>
                                                <td className="py-4 px-4">
                                                    <span
                                                        className="px-2 py-1 rounded text-xs font-semibold uppercase"
                                                        style={{ backgroundColor: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}` }}
                                                        data-testid={`student-status-${student.id}`}
                                                    >
                                                        {student.status || 'approved'}
                                                    </span>
                                                </td>
                                                <td className="py-4 px-4">
                                                    {student.broker ? (
                                                        <span className="px-2 py-1 rounded text-xs font-semibold uppercase" style={{ backgroundColor: `${brokerColor}18`, color: brokerColor, border: `1px solid ${brokerColor}40` }}>
                                                            {student.broker}
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                                                    )}
                                                    {student.unlock_method && student.status !== 'approved' && (
                                                        <p className="mt-1.5 text-[11px] font-black uppercase tracking-wide" style={{ color: student.unlock_path === 'paid' ? '#7C3AED' : 'var(--primary-color)' }} data-testid={`unlock-path-${student.id}`}>
                                                            {student.unlock_method.replace('_', ' ')}
                                                        </p>
                                                    )}
                                                    {(student.broker_link_clicks > 0 || student.broker_link_first_click) ? (
                                                        <p className="mt-1.5 text-[11px] font-bold flex items-center gap-1" style={{ color: '#059669' }} data-testid={`broker-clicked-${student.id}`}
                                                            title={`First click: ${student.broker_link_first_click ? new Date(student.broker_link_first_click).toLocaleString() : '—'}${student.broker_link_last_type ? ` · ${student.broker_link_last_type.toUpperCase()} link` : ''}`}>
                                                            <MousePointerClick className="w-3 h-3" />
                                                            Clicked ×{student.broker_link_clicks || 1}
                                                            {student.broker_link_last_type === 'ib' && <span className="px-1 rounded text-[9px]" style={{ backgroundColor: 'rgba(184,134,11,0.15)', color: 'var(--primary-color)' }}>IB</span>}
                                                        </p>
                                                    ) : (
                                                        <p className="mt-1.5 text-[11px]" style={{ color: 'var(--text-muted)' }} data-testid={`broker-noclick-${student.id}`}>no click</p>
                                                    )}
                                                </td>
                                                <td className="py-4 px-4">
                                                    {student.deposit_screenshot_url ? (
                                                        <a href={`${API}/files/${student.deposit_screenshot_url}`} target="_blank" rel="noreferrer" className="text-xs underline" style={{ color: 'var(--primary-color)' }} data-testid={`view-proof-${student.id}`}>
                                                            View
                                                        </a>
                                                    ) : (
                                                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>none</span>
                                                    )}
                                                </td>
                                                <td className="py-4 px-4">
                                                    {student.status === 'approved' ? (
                                                        <span
                                                            className="px-2 py-1 rounded text-xs font-semibold"
                                                            style={{
                                                                backgroundColor: `${getPhaseColor(student.current_phase)}15`,
                                                                color: getPhaseColor(student.current_phase),
                                                                border: `1px solid ${getPhaseColor(student.current_phase)}30`
                                                            }}
                                                        >
                                                            {formatPhaseName(student.current_phase)}
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                                                    )}
                                                </td>
                                                <td className="py-4 px-4">
                                                    <span style={{ color: 'var(--text-muted)' }}>{formatDate(student.enrolled_at)}</span>
                                                </td>
                                                <td className="py-4 px-4 text-center">
                                                    <span className="font-medium" style={{ color: 'var(--primary-color)' }}>{student.lessons_completed || 0}</span>
                                                </td>
                                                <td className="py-4 px-4">
                                                    <div className="flex items-center justify-end gap-1">
                                                        {student.status === 'pending' && (
                                                            <>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => approveStudent(student.id)}
                                                                    disabled={actionLoading}
                                                                    className="h-8 px-3"
                                                                    style={{ color: '#10B981' }}
                                                                    data-testid={`approve-student-${student.id}`}
                                                                >
                                                                    <Check className="w-4 h-4 mr-1" />
                                                                    Approve
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => setConfirmDialog({ open: true, action: 'reject', id: student.id, name: student.name })}
                                                                    className="h-8 px-3"
                                                                    style={{ color: '#EF4444' }}
                                                                    data-testid={`reject-student-${student.id}`}
                                                                >
                                                                    <X className="w-4 h-4 mr-1" />
                                                                    Reject
                                                                </Button>
                                                            </>
                                                        )}
                                                        {student.status === 'rejected' && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => approveStudent(student.id)}
                                                                disabled={actionLoading}
                                                                className="h-8 px-3"
                                                                style={{ color: '#10B981' }}
                                                                data-testid={`reapprove-student-${student.id}`}
                                                            >
                                                                <Check className="w-4 h-4 mr-1" />
                                                                Approve
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setConfirmDialog({ open: true, action: 'delete', id: student.id, name: student.name })}
                                                            data-testid={`delete-student-${student.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" style={{ color: 'var(--accent-danger)' }} />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </main>

            <AlertDialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: null, id: null, name: '' })}>
                <AlertDialogContent style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border-default)' }}>
                    <AlertDialogHeader>
                        <AlertDialogTitle style={{ color: 'var(--text-primary)' }}>
                            {confirmDialog.action === 'delete' ? 'Remove Student?' : 'Reject Student?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription style={{ color: 'var(--text-secondary)' }}>
                            {confirmDialog.action === 'delete'
                                ? `Remove "${confirmDialog.name}" and delete all their progress? This cannot be undone.`
                                : `Reject "${confirmDialog.name}"? They will see a rejection message on login. You can re-approve them later.`
                            }
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="btn-ghost">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDialog.action === 'delete' ? deleteStudent : rejectStudent}
                            style={{ backgroundColor: 'var(--accent-danger)' }}
                            data-testid="confirm-action-btn"
                        >
                            {confirmDialog.action === 'delete' ? 'Remove' : 'Reject'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
